import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Trash2, ShieldCheck, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/PageHeader";
import { ChangePassword } from "@/components/ChangePassword";
import { DeviceSessions } from "@/components/DeviceSessions";
import { api, errMsg, fmtDate, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Settings() {
  const { user, refresh, setUser } = useAuth();
  const navigate = useNavigate();
  const [rem, setRem] = useState(user?.email_reminders !== false);

  const toggleRem = async (v) => {
    setRem(v);
    await api.put("/me/preferences", { email_reminders: v });
    toast.success(v ? "Promemoria email attivati" : "Promemoria email disattivati");
    refresh();
  };
  const exportData = async () => {
    try {
      await downloadFile("/gdpr/export", "burocrazy-miei-dati.json");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const deleteAccount = async () => {
    try {
      await api.delete("/gdpr/account");
      setUser(null);
      toast.success("Account e dati eliminati");
      navigate("/");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Impostazioni" title="Account e privacy" desc="Controlla i tuoi dati in qualsiasi momento, come previsto dal GDPR." />
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="rounded-3xl border bg-white p-6">
          <h3 className="font-medium">Account</h3>
          <dl className="mt-4 text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-slate-500">Nome</dt><dd data-testid="settings-name">{user.name}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd data-testid="settings-email">{user.email}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Piano</dt><dd data-testid="settings-plan">{user.plan === "pro" ? "Pro" : "Free"} · <Link to="/app/pro" className="text-[#1E3A8A] underline">gestisci</Link></dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Iscritto dal</dt><dd>{fmtDate(user.created_at)}</dd></div>
          </dl>
        </div>
        {user.has_password && <ChangePassword />}
        <DeviceSessions />
        <div className="rounded-3xl border bg-white p-6">
          <h3 className="font-medium flex items-center gap-2"><Bell className="h-4 w-4" /> Notifiche</h3>
          <label className="mt-4 flex items-center justify-between text-sm gap-4">Ricevi i promemoria delle scadenze via email<Switch checked={rem} onCheckedChange={toggleRem} data-testid="settings-email-reminders-switch" /></label>
          <p className="mt-2 text-xs text-slate-500">I promemoria in app restano sempre attivi.</p>
        </div>
        <div className="rounded-3xl border bg-white p-6">
          <h3 className="font-medium flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#1E3A8A]" /> Consensi</h3>
          <p className="mt-3 text-sm text-slate-600">Termini e privacy accettati il {fmtDate(user.consents?.accepted_at)} (versione {user.consents?.version}).</p>
          <p className="text-sm text-slate-600">Comunicazioni promozionali: {user.consents?.marketing ? "sì" : "no"}.</p>
          <div className="mt-3 flex gap-4 text-sm"><Link to="/privacy" className="underline">Privacy</Link><Link to="/cookie" className="underline">Cookie</Link><Link to="/termini" className="underline">Termini</Link></div>
        </div>
        <div className="rounded-3xl border bg-white p-6">
          <h3 className="font-medium">I tuoi dati (GDPR)</h3>
          <p className="mt-2 text-sm text-slate-600">Scarica una copia di tutti i tuoi dati (diritto alla portabilità) o elimina definitivamente il tuo account (diritto all'oblio).</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button variant="outline" className="rounded-full" onClick={exportData} data-testid="gdpr-export-btn"><Download className="h-4 w-4 mr-2" /> Esporta i miei dati</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="destructive" className="rounded-full" data-testid="gdpr-delete-btn"><Trash2 className="h-4 w-4 mr-2" /> Elimina account</Button></AlertDialogTrigger>
              <AlertDialogContent data-testid="gdpr-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare definitivamente l'account?</AlertDialogTitle>
                  <AlertDialogDescription>Cancelleremo documenti, scadenze, profili famiglia, lettere e conversazioni. L'operazione non è reversibile. Se hai un abbonamento Pro, disdicilo prima.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="gdpr-delete-cancel">Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteAccount} className="bg-red-600 hover:bg-red-700" data-testid="gdpr-delete-confirm">Elimina tutto</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
}
